import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import ProductCard from '../components/ProductCard';
import './Home.css';

const TRUST_BADGES = [
  { icon: '🔬', title: '3rd Party Tested', desc: 'Every batch independently verified for purity' },
  { icon: '🚚', title: 'Free Shipping $150+', desc: 'Fast discreet shipping on qualifying orders' },
  { icon: '🏆', title: '99%+ Purity', desc: 'Pharmaceutical-grade research compounds' },
  { icon: '🔒', title: 'Secure Checkout', desc: 'SSL encrypted payments, crypto accepted' },
  { icon: '📞', title: '24/7 Support', desc: 'Expert research support team' },
  { icon: '↩️', title: '30-Day Guarantee', desc: 'Satisfaction guaranteed or money back' },
];

const WHY_US = [
  { icon: '⚗️', title: 'Superior Synthesis', desc: 'HPLC-purified peptides synthesized to exacting research standards using premium grade amino acid precursors.' },
  { icon: '📄', title: 'COA with Every Batch', desc: 'Certificate of Analysis from accredited third-party labs included with every product shipment.' },
  { icon: '❄️', title: 'Cold-Chain Logistics', desc: 'Temperature-controlled storage and shipping to preserve peptide integrity from our facility to yours.' },
  { icon: '🔬', title: 'Experienced Team', desc: 'Our team of biochemists and researchers ensures every product meets strict quality benchmarks.' },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    api.get('/products?featured=true&limit=8')
      .then(r => setFeatured(r.data.products))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="home">
      {/* Hero */}
      <section className="hero">
        <div className="hero__bg" />
        <div className="container hero__content">
          <span className="hero__label">🏆 #1 Research Peptide Supplier</span>
          <h1>Premium Research-Grade<br /><span>Peptides & Compounds</span></h1>
          <p>Third-party tested for purity and potency. Trusted by researchers worldwide. 99%+ purity guaranteed on every batch.</p>
          <div className="hero__cta">
            <Link to="/shop" className="btn btn-primary btn-lg">Shop Now →</Link>
            <Link to="/certificates" className="btn btn-secondary btn-lg">View Lab Reports</Link>
          </div>
          <div className="hero__stats">
            <div><strong>500+</strong><span>Products</span></div>
            <div><strong>10,000+</strong><span>Researchers</span></div>
            <div><strong>99.2%</strong><span>Avg. Purity</span></div>
            <div><strong>4.9★</strong><span>Rating</span></div>
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="trust-strip">
        <div className="container trust-grid">
          {TRUST_BADGES.map((b, i) => (
            <div key={i} className="trust-badge">
              <span>{b.icon}</span>
              <div>
                <strong>{b.title}</strong>
                <p>{b.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="section-title">Featured Research Compounds</h2>
            <p className="section-subtitle">Our most popular peptides, independently tested and researcher-verified</p>
          </div>
          {loading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : (
            <div className="grid-4">
              {featured.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
          <div className="text-center mt-8">
            <Link to="/shop" className="btn btn-primary btn-lg">View All Products →</Link>
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="why-us section" style={{ background: 'var(--gray-50)' }}>
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="section-title">Why Choose PeptideLabPro?</h2>
            <p className="section-subtitle">Research-grade quality you can trust, backed by science</p>
          </div>
          <div className="grid-4">
            {WHY_US.map((w, i) => (
              <div key={i} className="why-card">
                <div className="why-card__icon">{w.icon}</div>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer banner */}
      <div className="disclaimer-banner">
        <div className="container">
          <p>⚠️ <strong>For Research Use Only.</strong> All products are intended for laboratory research by qualified professionals. Not for human consumption, self-administration, or veterinary use. You must be 18+ to purchase.</p>
        </div>
      </div>
    </div>
  );
}
